import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, CheckCircle2, Mail, Clock } from "lucide-react";
import { findEmrBySlug } from "@/config/emrs";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/* Notify-me modal for SOON EMRs. Pre-selects the EMR via `slug` prop. */
export const NotifyEmrModal = ({ open, slug, onClose }) => {
  const emr = findEmrBySlug(slug);
  const [email, setEmail] = useState("");
  const [clinic, setClinic] = useState("");
  const [busy, setBusy] = useState(false);
  const [ok, setOk] = useState(false);
  const [err, setErr] = useState("");

  const reset = () => {
    setEmail("");
    setClinic("");
    setOk(false);
    setErr("");
    setBusy(false);
  };

  const submit = async (e) => {
    e.preventDefault();
    if (!emr) return;
    setBusy(true);
    setErr("");
    try {
      const res = await fetch(`${API}/integrations/notify`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          email,
          emr: emr.name,
          clinic_name: clinic || null,
        }),
      });
      if (!res.ok) throw new Error("Subscribe failed");
      setOk(true);
    } catch (e2) {
      setErr("Couldn't subscribe — please try again.");
    } finally {
      setBusy(false);
    }
  };

  const close = () => {
    reset();
    onClose();
  };

  return (
    <AnimatePresence>
      {open && emr && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[100] flex items-center justify-center overflow-y-auto bg-black/65 px-4 py-8 backdrop-blur-sm"
          onClick={close}
          data-testid="notify-modal-backdrop"
        >
          <motion.div
            initial={{ opacity: 0, scale: 0.96, y: 18 }}
            animate={{ opacity: 1, scale: 1, y: 0 }}
            exit={{ opacity: 0, scale: 0.97, y: 12 }}
            transition={{ duration: 0.25 }}
            onClick={(e) => e.stopPropagation()}
            className="relative w-full max-w-[460px]"
            data-testid="notify-modal"
          >
            <div className="overflow-hidden rounded-2xl border border-border bg-card shadow-2xl">
              {/* Header — centered title with close in top-right */}
              <div className="relative border-b border-border px-6 py-5 text-center">
                <button
                  type="button"
                  onClick={close}
                  aria-label="Close"
                  data-testid="notify-modal-close"
                  className="absolute right-4 top-4 rounded-md p-1.5 text-muted-foreground transition-colors hover:bg-secondary hover:text-foreground"
                >
                  <X className="h-4 w-4" />
                </button>

                <img
                  src={emr.logo}
                  alt={`${emr.name} logo`}
                  className="mx-auto h-10 w-10 rounded-lg object-contain"
                />
                <p className="mt-3 inline-flex items-center gap-1 rounded-full bg-amber-500/10 px-2.5 py-0.5 text-[10px] font-bold uppercase tracking-[0.18em] text-amber-700 dark:text-amber-400">
                  <Clock className="h-2.5 w-2.5" /> Coming soon
                </p>
                <h3 className="mt-2 font-display text-xl font-black tracking-[-0.02em] sm:text-2xl">
                  {emr.name} integration
                </h3>
              </div>

              {/* Body */}
              <div className="px-6 py-6 sm:px-7 sm:py-7">
                {ok ? (
                  <div className="text-center" data-testid="notify-success">
                    <span className="mx-auto inline-flex h-14 w-14 items-center justify-center rounded-full bg-emerald-500/15 text-emerald-600 dark:text-emerald-400">
                      <CheckCircle2 className="h-7 w-7" />
                    </span>
                    <h3 className="mt-5 font-display text-xl font-black tracking-[-0.02em]">
                      You&apos;re on the list.
                    </h3>
                    <p className="mx-auto mt-3 max-w-xs text-sm leading-relaxed text-muted-foreground">
                      We&apos;ll email{" "}
                      <span className="font-semibold text-foreground">{email}</span>{" "}
                      the moment the {emr.name} integration goes live.
                    </p>
                    <button
                      type="button"
                      onClick={close}
                      data-testid="notify-modal-done"
                      className="mt-6 inline-flex h-11 items-center justify-center rounded-lg bg-foreground px-6 text-sm font-semibold text-background transition-all hover:-translate-y-0.5 hover:opacity-90"
                    >
                      Got it
                    </button>
                  </div>
                ) : (
                  <form onSubmit={submit} className="space-y-4" data-testid="notify-form">
                    <p className="text-center text-sm leading-relaxed text-muted-foreground">
                      We&apos;re finishing the {emr.name} integration. Drop your email
                      and we&apos;ll ping you the moment it&apos;s live.
                    </p>

                    {/* Email */}
                    <div>
                      <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                        Email
                      </label>
                      <div className="relative mt-1.5">
                        <Mail className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-muted-foreground" />
                        <input
                          type="email"
                          required
                          autoFocus
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="you@yourclinic.com"
                          data-testid="notify-email-input"
                          className="block w-full rounded-lg border border-border bg-background py-2.5 pl-9 pr-3 text-sm outline-none ring-ring transition-all focus:ring-2"
                        />
                      </div>
                    </div>

                    {/* Clinic name (optional) */}
                    <div>
                      <label className="text-[11px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                        Clinic name{" "}
                        <span className="font-normal normal-case text-muted-foreground/70">
                          · optional
                        </span>
                      </label>
                      <input
                        type="text"
                        value={clinic}
                        onChange={(e) => setClinic(e.target.value)}
                        placeholder="Apex Physio"
                        data-testid="notify-clinic-input"
                        className="mt-1.5 block w-full rounded-lg border border-border bg-background px-3 py-2.5 text-sm outline-none ring-ring transition-all focus:ring-2"
                      />
                    </div>

                    {err && (
                      <p
                        className="text-center text-xs font-medium text-rose-500"
                        data-testid="notify-error"
                      >
                        {err}
                      </p>
                    )}

                    <button
                      type="submit"
                      disabled={busy}
                      data-testid="notify-submit"
                      className="inline-flex h-11 w-full items-center justify-center gap-2 rounded-lg bg-foreground text-sm font-semibold text-background transition-all hover:opacity-90 disabled:opacity-50"
                    >
                      {busy ? "Adding you to the list…" : `Notify me when ${emr.name} is ready`}
                    </button>

                    <p className="text-center text-[10px] text-muted-foreground">
                      One email when it ships. No spam. Unsubscribe anytime.
                    </p>
                  </form>
                )}
              </div>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
